import isLoaded from './isLoaded';
import addLoaded from './addLoaded';
import StaticRasterManager from './StaticRasterManager';

const TILE_SIZE = 256;

const ascend = (a, b) => a - b;
const getGapX = (xs) => {
  let gap = 1;

  for (let i = 1; i < xs.length; i += 1) {
    gap = xs[i] - xs[i - 1];
    if (gap > 1) {
      return xs[i];
    }
  }

  return -1;
};
const getX = ({ coord }) => coord.x;
const getY = ({ coord }) => coord.y;
const setUpdated2False = (tile) => {
  tile.isRasterUpdated = false; // eslint-disable-line no-param-reassign
};

/**
 * RaserManager to handle dynamic elevation change.
 * @implements {google.maps.MapType}
 */
export default class DynamicRasterManager extends StaticRasterManager {
  /**
   * @param {!Object} props Properties.
   * @param {!array<float>} altitudes altitude steps.
   * @param {!float} altitude current altitude.
   * @param {number} blur distance in pixels.
   * @param {!function} updateProgress, updateLoading progress.
   */
  constructor(props) {
    super(props);

    const {
      altitudes,
      altitude,
    } = props;

    this.altitudes = altitudes;

    this.overlay = null;
    this.clipMax = 14;

    this.setClip(altitude);
    this.helperCanvas = document.createElement('canvas');

    this.updateTileRaster = this.updateTileRaster.bind(this);
  }

  /**
   * Set clip value from altitude.
   * @param {!number} altitude Sea level rice indicator.
   */
  setClip(altitude) {
    let lower = 0.0;
    let upper = 0.0;

    for (let i = 0; i < this.altitudes.length; i += 1) {
      if (this.altitudes[i] >= altitude) {
        upper = this.altitudes[i];

        if (i > 0) {
          lower = this.altitudes[i - 1];
        } else {
          lower = 0;
        }

        break;
      }
    }

    if (upper < 2.0) {
      upper = 2.0;
    }

    if (lower < 2.0) {
      lower = 0.0;
    }

    this.altitude = altitude;
    this.trueClip = 14 + lower * 2;
    this.clipMax = 14 + upper * 2;
    this.partialOpacity = ((this.altitude - lower) / (upper - lower)) * 255;

    this.activeTiles.forEach(setUpdated2False);
  }

  /**
   * Set current progress loading tiles.
   * Warning: fully 100% loaded has to be set by hand.
   */
  setProgress() {
    const { activeTiles, updateProgress } = this;
    const ratio = activeTiles.reduce(addLoaded, 0) / (activeTiles.length + 1);
    updateProgress(ratio * 100);
  }

  /**
   * @param {!RasterTile} tile Tile to be processed.
   * @return {HTMLCanvasElement} Processed displayable image.
   */
  processRasterTile(tile) {
    const { imageEl, processedCanvas } = tile;

    if (!imageEl) {
      // Always return the canvas of the tile for rendering.
      return processedCanvas;
    }

    this.updateTileRaster(tile);
    this.setProgress();

    if (this.activeTiles.every(isLoaded)) {
      this.updateOnlyRaster();
      // Progress is 100% done when the drawing is done.
      this.updateProgress(100.0);
    }

    return processedCanvas;
  }

  /**
   * Update tile raster canvas.
   * @param {RasterTile} tile Tile to get updated.
   */
  updateTileRaster(tile) {
    const {
      clipMax,
      partialOpacity,
      trueClip,
    } = this;

    const {
      imageEl,
      isRasterUpdated,
      processedCanvas,
      rasterCanvas,
    } = tile;

    const width = TILE_SIZE;
    const height = TILE_SIZE;

    if (isRasterUpdated || !imageEl) {
      return;
    }

    const ctx = rasterCanvas.getContext('2d');

    rasterCanvas.width = width;
    rasterCanvas.height = height;
    processedCanvas.width = width;
    processedCanvas.height = height;
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(imageEl, 0, 0);
    const imageData = ctx.getImageData(0, 0, width, height);
    const { data } = imageData;
    const { length } = data;

    let red = 0;
    for (let i = 0; i < length; i += 4) {
      red = data[i];

      if (red > clipMax || red < 1) {
        data[i + 3] = 0;
      } else {
        data[i] = 255;
        data[i + 1] = 255;
        data[i + 2] = 255;
        data[i + 3] = red <= trueClip ? 255 : partialOpacity;
      }
    }

    ctx.putImageData(imageData, 0, 0);

    const proCtx = processedCanvas.getContext('2d');
    proCtx.putImageData(imageData, 0, 0);

    tile.isRasterUpdated = true; // eslint-disable-line no-param-reassign
  }

  /**
   * Update raster tiles.
   */
  updateOnlyRaster() {
    const { activeTiles, blur } = this;
    const { length } = activeTiles;
    const width = TILE_SIZE;
    const height = TILE_SIZE;
    const xs = Array
        .from((new Set(activeTiles.map(getX))).values())
        .sort(ascend);
    const ys = Array
        .from((new Set(activeTiles.map(getY))).values())
        .sort(ascend);
    const maxX = xs[xs.length - 1];
    const minX = xs[0];
    const minY = ys[0];
    const gapX = getGapX(xs);
    const isGap = gapX !== -1;

    this.helperCanvas.width = (xs.length + 1) * width;
    this.helperCanvas.height = (ys.length + 1) * height;

    // Fix the error at when coord.x = 0
    // The tiles are not in growing order
    // x coord will be e.g. 0, 1, 2, 31, 30, 29
    const dx = isGap ? maxX - gapX : 0;

    const context = this.helperCanvas.getContext('2d');
    context.clearRect(0, 0, this.helperCanvas.width, this.helperCanvas.height);

    if (isGap) {
      for (let i = 0; i < length; i += 1) {
        const { coord, rasterCanvas } = activeTiles[i];
        const xx = coord.x < gapX ? dx + coord.x + 1 : dx - (coord.x - gapX);
        context.drawImage(
            rasterCanvas,
            xx * width,
            (coord.y - minY) * height,
        );
      }
    } else {
      for (let i = 0; i < length; i += 1) {
        const { coord, rasterCanvas } = activeTiles[i];
        context.drawImage(
            rasterCanvas,
            (coord.x - minX - dx) * width,
            (coord.y - minY) * height,
        );
      }
    }

    context.filter = undefined;
    context.drawImage(this.helperCanvas, 0, 0);

    context.filter = `blur(${blur}px)`;
    context.drawImage(this.helperCanvas, 0, 0);
    context.drawImage(this.helperCanvas, 1, 1);
    context.drawImage(this.helperCanvas, -1, -1);

    if (blur > 3) {
      context.drawImage(this.helperCanvas, 1, 0);
    }

    context.filter = undefined;

    if (isGap) {
      // minX = 0 causes rendering issue:
      // huge gap between minX and maxY
      for (let i = 0; i < length; i += 1) {
        const { coord, processedCanvas } = activeTiles[i];
        const ctx = processedCanvas.getContext('2d');
        const xx = coord.x < gapX ? dx + coord.x + 1 : dx - (coord.x - gapX);
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(
            this.helperCanvas,
            xx * width,
            (coord.y - minY) * height,
            width, height,
            0, 0,
            width, height,
        );
      }
    } else {
      for (let i = 0; i < length; i += 1) {
        const { coord, processedCanvas } = activeTiles[i];
        const ctx = processedCanvas.getContext('2d');
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(
            this.helperCanvas,
            (coord.x - minX) * width,
            (coord.y - minY) * height,
            width, height,
            0, 0,
            width, height,
        );
      }
    }
  }

  /**
   * Update raster tiles.
   */
  update() {
    this.activeTiles.forEach(this.updateTileRaster);
    this.updateOnlyRaster();
  }
}
